import { Data } from '../components/TreeCheckbox/types'

const jsonHeaders: RequestInit = {
    credentials: 'same-origin',
    headers: {
        'Content-Type': 'application/json'
    }
}

const fileHeaders: RequestInit = {
    credentials: 'same-origin'
}

export const apiFetchFile = (url: string) => {
    const headers = fileHeaders
    return {
        get: () =>
            fetch(url, {
                method: 'GET',
                ...headers
            }),
        post: (body: any) =>
            fetch(url, {
                method: 'POST',
                ...headers,
                body
            }),
        patch: (body: any) =>
            fetch(url, {
                method: 'PATCH',
                ...headers,
                body
            }),
        put: (body: any) =>
            fetch(url, {
                method: 'PUT',
                ...headers,
                body
            }),
        delete: () =>
            fetch(url, {
                method: 'DELETE',
                ...headers
            })
    }
}

export const apiFetch = (url: string) => {
    const headers = jsonHeaders

    return {
        get: () =>
            fetch(url, {
                method: 'GET',
                ...headers
            }),
        getBody: (body: any) =>
            fetch(url, {
                method: 'POST',
                ...headers,
                body: JSON.stringify(body)
            }),
        post: (body: any) =>
            fetch(url, {
                method: 'POST',
                ...headers,
                body: JSON.stringify(body)
            }),
        patch: (body: any) =>
            fetch(url, {
                method: 'PATCH',
                ...headers,
                body: JSON.stringify(body)
            }),
        put: (body: any) =>
            fetch(url, {
                method: 'PUT',
                ...headers,
                body: JSON.stringify(body)
            }),
        delete: (body?: any) =>
            fetch(url, {
                method: 'DELETE',
                ...headers,
                body: body ? JSON.stringify(body) : undefined
            })
    }
}

export const apiRequest = <T = any>(
    url: string,
    onSuccess: (data: T) => void,
    onError: (error: string) => void,
    onFinally?: () => void
) => {
    const headers = jsonHeaders

    const responseHandler = async (requestParameters: RequestInit) => {
        try {
            const response = await fetch(url, requestParameters)

            const responseBody = await response.json()

            if (!response.ok) {
                throw responseBody.message
            } else {
                await onSuccess(responseBody as T)
            }
        } catch (error) {
            await onError(error as string)
        } finally {
            if (onFinally) await onFinally()
        }
    }

    return {
        get: () => {
            responseHandler({
                method: 'GET',
                ...headers
            })
        },
        getBody: (body: any) => {
            responseHandler({
                method: 'POST',
                ...headers,
                body: JSON.stringify(body)
            })
        },
        post: (body: any) => {
            responseHandler({
                method: 'POST',
                ...headers,
                body: JSON.stringify(body)
            })
        },
        patch: (body: any) => {
            responseHandler({
                method: 'PATCH',
                ...headers,
                body: JSON.stringify(body)
            })
        },
        put: (body: any) => {
            responseHandler({
                method: 'PUT',
                ...headers,
                body: JSON.stringify(body)
            })
        },
        delete: (body?: any) => {
            responseHandler({
                method: 'DELETE',
                ...headers,
                body: body ? JSON.stringify(body) : undefined
            })
        }
    }
}


export const isValidNumber = (value: string | number) => {
    const condition = new RegExp(/^\d{1,}([.]\d{0,})*$/)
    return condition.test(String(value))
}

export const isValidInputDate = (value: string) => {
    const condition = new RegExp(
        /^(\d{2}\/\d{2}\/\d{1,4})$|^(\d{2}\/\d{2}\/)$|^(\d{2}\/\d{1,2})$|^(\d{2}\/)$|^(\d{1,2})$/
    )
    return condition.test(value)
}

type Node = {
    name: string
    children?: Array<Node>
    [key: string]: any
}

export const filterTree = (data: Array<Node>, string: string): Array<Node> => {
    string = string.toLowerCase()
    return data
        .map(node => ({ ...node }))
        .filter(node => {
            if (node.children) {
                return (
                    node.name.toLowerCase().includes(string) ||
                    (node.children = filterTree(node.children, string)).length
                )
            } else {
                return node.name.toLowerCase().includes(string)
            }
        })
}

const getRandomColor = () => {
    var letters = '0123456789ABCDEF'
    var color = '#'
    for (var i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
    }
    return color
}

export const getLabelColor = (index: number) => {
    const colors: any = {
        0: '#F89300',
        1: '#01C292',
        2: '#37A3EF',
        3: '#3fcdfc',
        4: '#42b864',
        5: '#eb9b01',
        6: '#f44236',
        7: '#9c27b1',
        8: '#2b0ec5',
        9: '#02a9f5',
        10: '#018d7f',
        11: '#8bc24a',
        12: '#ffeb3d',
        13: '#ff9702',
        14: '#795547',
        15: '#607d8b',
        16: '#ea1e63',
        17: '#673bb7',
        18: '#0e73c3',
        19: '#34d1e6',
        20: '#309535',
        21: '#cddc3a',
        22: '#fec10a',
        23: '#fe5722',
        24: '#9e9e9e',
        25: '#d8d8d8'
    }

    if (colors[index]) {
        return colors[index]
    } else {
        return getRandomColor()
    }
}

export const moneyFormat = (value: number | string, currency: string = '$') => {
    return `${currency} ${Number(value)
        .toFixed(2)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`
}
export const currencyFormatter = (value: string | number) => {
    let newValue = 0
    if (!isNaN(value as any)) newValue = Number(value)
    return newValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export const contains = (text: string, term: string) => {
    return text.toUpperCase().includes(term.toUpperCase())
}

export const isDateString = (dateString: string) => !!Date.parse(dateString)

export const filterTreeCheckbox = (data: Data[], filterTerm: string, level = 0) => {
    let treeData: Data[] = []
    let hasChildVisible = false
    for (const item of data) {
        let visible = false
        let filteredChildren: Data[] = []

        if (contains(item.name, filterTerm)) visible = true

        if (item.children) {
            const children: any = filterTreeCheckbox(item.children, filterTerm, level + 1)
            filteredChildren = children.treeData
            if (children.hasChildVisible) visible = true
        }

        if (visible) hasChildVisible = true
        treeData.push({ ...item, visible, children: filteredChildren })
    }
    return level ? { treeData, hasChildVisible } : treeData
}

export function formatCurrency(value: number) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

export const percentile = (arr: number[], q: number) => {
    if (arr) {
        const position = (arr.length - 1) * q
        const base = Math.floor(position)
        const rest = position - base
        if (arr[base + 1] !== undefined) {
            return arr[base] + rest * (arr[base + 1] - arr[base])
        } else {
            return arr[base]
        }
    }
    return null
}

export const generateFloorColumnValue = (row: any) => {
    if (row.derricksMap && Object.keys(row.derricksMap).length > 0) {
        const keys = Object.keys(row.derricksMap)
        let floorValue = row.derricksMap[keys[0]].name
        keys.slice(1).forEach(key => {
            floorValue += ' / ' + row.derricksMap[key].name
        })
        return floorValue
    }
    return '---'
}

// Function to parser table data to table tree format
export function generatedTableTreeData(originalData: any, quantTraveledNodes = 0, layerDepth = 0, baseLayerDepth = 0) {
    const convertedData = []
    let lastConvertedPosition = 0
    let checkChildren = false
    let lastLayerDepth = 0
    let lastQuant = quantTraveledNodes
    let item
    for (let itemPosition = 0; itemPosition < originalData.length; itemPosition++) {
        item = originalData[itemPosition]

        // Temporary setup of floor value
        if (!item.floor) {
            item.floor = generateFloorColumnValue(item)
        }

        if (item.level) {
            item.layerDepth = item.level
        }

        // If its the checked layerDepth, than convert
        if (item.layerDepth === layerDepth) {
            convertedData.push({
                ...item
                // position: itemPosition
            })
            lastQuant = lastQuant + 1
            lastConvertedPosition++
            checkChildren = true
        }
        // Verifies if stopped checking children
        if (
            (item.layerDepth < lastLayerDepth &&
                layerDepth !== baseLayerDepth &&
                item.layerDepth !== layerDepth &&
                layerDepth !== 1) ||
            itemPosition + 1 >= originalData.length
        ) {
            break
        }
        // If needs to check children, than call this function using recursion
        if (
            checkChildren &&
            (item.layerDepth === originalData[itemPosition + 1].layerDepth - 1 ||
                item.level === originalData[itemPosition + 1].level - 1)
        ) {
            const childrenTableData = generatedTableTreeData(
                originalData.slice(itemPosition + 1),
                lastQuant,
                item.layerDepth + 1,
                baseLayerDepth
            )

            convertedData[lastConvertedPosition - 1].children = childrenTableData.data
            // skips originalData values based on amount of children passed
            itemPosition += childrenTableData.quantTraveledNodes - lastQuant
            lastQuant = childrenTableData.quantTraveledNodes
            checkChildren = false
        }
        // Used to compare last layerDepth and actual layerDepth and check if stopped checking for children
        lastLayerDepth = item.layerDepth
    }

    return {
        quantTraveledNodes: lastQuant,
        data: convertedData
    }
}
type ParamType = string | number | boolean

export const encodeURLParam = (key: string, value: ParamType) => `${key}=${encodeURIComponent(value)}`

export const buildSingleURLParam = (key: string, value: ParamType, url = '') =>
    `${url}${url ? '&' : '?'}${encodeURLParam(key, value)}`

export const buildMultipleURLParam = (key: string, values: ParamType[], url = '') => {
    if (values) {
        for (const v of values) {
            url = buildSingleURLParam(key, v, url)
        }
    }

    return url
}
